import { useState } from "react";
import { X, Download, ExternalLink } from "lucide-react";

import { saveFile } from "@/lib/download";

/**
 * View a stored PDF or image without leaving the app.
 *
 * This used to render the file by pointing an iframe at
 * `https://mozilla.github.io/pdf.js/web/viewer.html?file=<signed url>`, which
 * handed a third-party page a working read link to the shop's USPS paperwork
 * on every preview - and put that link in their request logs. Browsers have
 * rendered PDFs natively for years, so the iframe points at the file itself
 * and nothing leaves this origin.
 *
 * `downloadUrl` is a URL storage will serve as an attachment. Without one the
 * bytes are fetched and saved through a blob, because the `download` attribute
 * does nothing on a cross-origin href.
 */
export default function PDFViewerModal({ pdfUrl, title, onClose, downloadUrl, contentType }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const isImage = (contentType ?? "").startsWith("image/");
  const extension = isImage ? (contentType.split("/")[1] || "png") : "pdf";
  const filename = /\.[A-Za-z0-9]+$/.test(title) ? title : `${title}.${extension}`;

  const handleDownload = async () => {
    setError("");

    if (downloadUrl) {
      // Storage sets Content-Disposition, so a plain navigation saves the file.
      window.location.href = downloadUrl;
      return;
    }

    setDownloading(true);
    try {
      await saveFile(pdfUrl, filename);
    } catch (err) {
      setError(err.message || "Could not download the file.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="file-viewer" onClick={(e) => e.stopPropagation()}>
        <div className="file-viewer-head">
          <h3 title={title}>{title}</h3>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-outline"
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <ExternalLink size={16} />
              Open in tab
            </a>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleDownload}
              disabled={downloading}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <Download size={16} />
              {downloading ? "Downloading..." : "Download"}
            </button>
            <button onClick={onClose} className="icon-button" aria-label="Close">
              <X size={24} />
            </button>
          </div>
        </div>

        {error && <div className="alert alert-error" style={{ margin: "0.75rem 1.5rem 0" }}>{error}</div>}

        <div className="file-viewer-body">
          {isImage ? (
            <img src={pdfUrl} alt={title} className="file-viewer-image" />
          ) : (
            <iframe src={pdfUrl} title={title} className="file-viewer-frame" />
          )}
        </div>
      </div>
    </div>
  );
}
